<template>
  <h3>
    Registros diarios de
    <span class="span-homicidios">homicidios</span>
    y
    <span class="span-desapariciones">desapariciones</span>
    de jóvenes en cada sexenio
  </h3>
  <p class="nota">
    Se muestra la evolución del promedio diario de crímenes contra los jóvenes a
    lo largo de 16 años. Coloca el cursor sobre los puntos para obtener más
    detalles.
  </p>
  <div id="contenedor_lineas">
    <div id="tooltipDiarios" class="tultip">
      En promedio, en <span class="highlight"> {{ anio_tultip }}</span> se
      registraron <span class="highlight">{{ numero_tultip }}</span>
      <span class="highlight">{{ fenomeno_tultip }}</span
      >.
    </div>
    <svg
      id="svg_lineas"
      :width="ancho + margenes.derecha + margenes.izquierda"
      :height="alto_svg"
    >
      <g
        class="eje-x"
        :transform="`translate(${margenes.izquierda},${
          margenes.arriba + alto
        })`"
      ></g>
      <g
        class="eje-y"
        :transform="`translate(${margenes.izquierda},${margenes.arriba})`"
      ></g>
      <g
        class="grupo-lineas"
        :transform="`translate(${margenes.izquierda},${margenes.arriba})`"
      ></g>
      <g
        class="grupo-circulos-homicidios"
        :transform="`translate(${margenes.izquierda},${margenes.arriba})`"
      ></g>
      <g
        class="grupo-circulos-desapariciones"
        :transform="`translate(${margenes.izquierda},${margenes.arriba})`"
      ></g>
      <g
        class="grid-group"
        :transform="`translate(${margenes.izquierda},${margenes.arriba})`"
      ></g>
    </svg>
  </div>
  <!--   <p class="pie">
  </p> -->
  <p class="fuente">
    El número de registros mostrado en el tooltip está redondeado.
    <br />
    <span class="highlight">Fuente</span>: Elaboración propia a partir de datos
    del INEGI y del RNPDNO.
  </p>
</template>
<script setup>
import * as d3 from "d3";
import { onMounted, onUnmounted, ref } from "vue";
import datos from "../assets/delitos_diarios.json";

const data = ref(datos);
const color_dict = ref({ desaparicion: "#d0a74e", homicidio: "#ed594a" });

const contenedor_svg = ref();
const svg = ref();
const ancho = ref(0);
const alto_svg = ref(400);
const alto = ref(0);
const margenes = ref({
  derecha: 30,
  izquierda: 60,
  abajo: 50,
  arriba: 60,
});

const maxX = ref();
const formatoTemporal = ref(d3.timeParse("%Y"));
const eje_X = ref();
const eje_Y = ref();
const grupo_lineas = ref();
const escalaX = ref();
const escalaY = ref();
const texto_x = ref();
const texto_y = ref();
const grupo_circulos_homicidios = ref();
const grupo_circulos_desapariciones = ref();

const radio_circulo = ref();

const vertical_grid = ref();
const data_vertical_grid = ref([
  formatoTemporal.value(2007),
  formatoTemporal.value(2013),
  formatoTemporal.value(2019),
]);
const data_leyendas_verticales = ref([
  { anio: 2007, texto_A: "Felipe Calderón", texto_B: "Felipe Calderón" },
  { anio: 2013, texto_A: "Enrique Peña Nieto", texto_B: "Enrique Peña" },
  { anio: 2019, texto_A: "AMLO", texto_B: "AMLO" },
]);

const tooltipDiarios = ref();
const numero_tultip = ref();
const fenomeno_tultip = ref();
const anio_tultip = ref();

onMounted(() => {
  contenedor_svg.value = document.getElementById(`contenedor_lineas`);
  svg.value = d3.select(`svg#svg_lineas`);
  eje_X.value = svg.value.select("g.eje-x");
  eje_Y.value = svg.value.select("g.eje-y");
  grupo_lineas.value = svg.value.select("g.grupo-lineas");
  grupo_circulos_homicidios.value = svg.value.select(
    "g.grupo-circulos-homicidios"
  );
  grupo_circulos_desapariciones.value = svg.value.select(
    "g.grupo-circulos-desapariciones"
  );
  vertical_grid.value = svg.value.select("g.grid-group");
  texto_x.value = svg.value.append("text").attr("text-anchor", "end");
  texto_y.value = svg.value.append("text").attr("text-anchor", "end");

  tooltipDiarios.value = d3.select("div#tooltipDiarios");
  tooltipDiarios.value.attr("class", "tultip").style("visibility", "hidden");

  maxX.value = d3.max(
    data.value.map((d) => d3.max(d.values.map((dd) => dd.no_casos)))
  );

  dimensionandingLineas();
  graficaLineas();
  window.addEventListener("resize", reescalanding);
});
onUnmounted(() => {
  window.removeEventListener("resize", reescalanding);
});

function reescalanding() {
  dimensionandingLineas();
  graficaLineas();
}
function dimensionandingLineas() {
  ancho.value =
    contenedor_svg.value.clientWidth -
    margenes.value.derecha -
    margenes.value.izquierda;
  alto.value = alto_svg.value - margenes.value.arriba - margenes.value.abajo;
  radio_circulo.value = 5;

  escalaX.value = d3
    .scaleTime()
    .domain([formatoTemporal.value(2006), formatoTemporal.value(2022)])
    .range([0, ancho.value]);
  escalaY.value = d3
    .scaleLinear()
    .domain([0, maxX.value])
    .range([alto.value, 0]);

  eje_X.value.call(d3.axisBottom(escalaX.value));
  eje_Y.value.call(d3.axisLeft(escalaY.value));

  texto_x.value
    .attr("text-anchor", "middle")
    .attr(
      "x",
      (ancho.value + margenes.value.izquierda + margenes.value.derecha) / 2
    )
    .attr("y", alto.value + margenes.value.arriba + margenes.value.abajo - 10)
    .text("Año")
    .attr("font-size", "14")
    .attr("font-family", "Avenir, Helvetica, Arial, sans-serif")
    .attr("font-weight", "bold");

  texto_y.value
    .attr("transform", "rotate(-90)")
    .attr("text-anchor", "middle")
    .attr("y", 25)
    .attr("x", -(alto.value + margenes.value.arriba + margenes.value.abajo) / 2)
    .attr("font-size", "14")
    .attr("font-weight", "bold")
    .attr("font-family", "Avenir, Helvetica, Arial, sans-serif")
    .text("Número de registros");

  // Agregamos las líneas verticales para marcar los años de cada gobierno
  //console.log("ticks: ", escalaX.value.ticks(3));
  vertical_grid.value
    .selectAll("line.verticalGrid")
    .data(data_vertical_grid.value)
    .join(
      (enter) => {
        enter
          .append("line")
          .attr("class", "verticalGrid")
          .attr("y1", alto_svg.value - 2 * margenes.value.abajo - 10)
          .attr("y2", 0 - 35)
          .attr("x1", (d) => escalaX.value(d))
          .attr("x2", (d) => escalaX.value(d))
          .attr("fill", "none")
          .attr("stroke", "black")
          .attr("stroke-width", "5px")
          .attr("opacity", 0.5);
      },
      (update) => {
        update
          .transition()
          .attr("class", "verticalGrid")
          .attr("y1", alto_svg.value - 2 * margenes.value.abajo - 10)
          .attr("y2", 0 - 35)
          .attr("x1", (d) => escalaX.value(d))
          .attr("x2", (d) => escalaX.value(d))
          .attr("fill", "none")
          .attr("stroke", "black")
          .attr("stroke-width", "3px");
      },
      (exit) => {
        exit.remove();
      }
    );

  // Agregamos las leyendas
  vertical_grid.value
    .selectAll("text.leyendGrid")
    .data(data_leyendas_verticales.value)
    .join(
      (enter) => {
        enter
          .append("text")
          .attr("class", "leyendGrid")
          .attr("y", 0 - 15)
          .attr("x", (d) => {
            if (ancho.value > 769) {
              return (
                escalaX.value(formatoTemporal.value(d.anio)) +
                (1 / 10) * ancho.value
              );
            } else {
              return escalaX.value(formatoTemporal.value(d.anio)) + 5;
            }
          })
          .attr("fill", "black")
          .attr("font-size", "12")
          .attr("text-anchor", ancho.value > 769 ? "middle" : "start")
          .style("font-weight", "bold")
          .text((d) => {
            if (ancho.value > 769) {
              return d.texto_A;
            } else {
              return d.texto_B;
            }
          });
      },
      (update) => {
        update
          .transition()
          .attr("class", "leyendGrid")
          .attr("y", 0 - 15)
          .attr("x", (d) => {
            if (ancho.value > 769) {
              return (
                escalaX.value(formatoTemporal.value(d.anio)) +
                (1 / 10) * ancho.value
              );
            } else {
              return escalaX.value(formatoTemporal.value(d.anio)) + 5;
            }
          })
          .attr("fill", "black")
          .attr("font-size", "12")
          .attr("text-anchor", ancho.value > 769 ? "middle" : "start")
          .style("font-weight", "bold")
          .text((d) => {
            if (ancho.value > 769) {
              return d.texto_A;
            } else {
              return d.texto_B;
            }
          });
      },
      (exit) => {
        exit.remove();
      }
    );
}

function graficaLineas() {
  //console.log("dibujanding lineas");
  grupo_lineas.value
    .selectAll("path")
    .data(data.value)
    .join(
      (enter) => {
        enter
          .append("path")
          .attr("stroke", (d) => color_dict.value[d.key])
          .attr("stroke-width", 3)
          .attr("fill", "none")
          .attr("d", (d) => {
            return d3
              .line()
              .x((d) => escalaX.value(formatoTemporal.value(d.anio)))
              .y((d) => escalaY.value(d.no_casos))(d.values);
          });
      },
      (update) => {
        update
          .transition()
          .attr("stroke", (d) => color_dict.value[d.key])
          .attr("stroke-width", 3)
          .attr("fill", "none")
          .attr("d", (d) => {
            return d3
              .line()
              .x((d) => escalaX.value(formatoTemporal.value(d.anio)))
              .y((d) => escalaY.value(d.no_casos))(d.values);
          });
      },
      (exit) => {
        exit.remove();
      }
    );

  grupo_circulos_homicidios.value
    .selectAll("circle")
    .data(data.value[0].values)
    .join(
      (enter) => {
        enter
          .append("circle")
          .attr("fill", color_dict.value["homicidio"])
          .attr("cx", (d) => escalaX.value(formatoTemporal.value(d.anio)))
          .attr("cy", (d) => escalaY.value(d.no_casos))
          .attr("r", radio_circulo.value);
      },
      (update) => {
        update
          .transition()
          .attr("fill", color_dict.value["homicidio"])
          .attr("cx", (d) => escalaX.value(formatoTemporal.value(d.anio)))
          .attr("cy", (d) => escalaY.value(d.no_casos))
          .attr("r", radio_circulo.value);
      },
      (exit) => {
        exit.remove();
      }
    );

  grupo_circulos_desapariciones.value
    .selectAll("circle")
    .data(data.value[1].values)
    .join(
      (enter) => {
        enter
          .append("circle")
          .attr("fill", color_dict.value["desaparicion"])
          .attr("cx", (d) => escalaX.value(formatoTemporal.value(d.anio)))
          .attr("cy", (d) => escalaY.value(d.no_casos))
          .attr("r", radio_circulo.value);
      },
      (update) => {
        update
          .transition()
          .attr("fill", color_dict.value["desaparicion"])
          .attr("cx", (d) => escalaX.value(formatoTemporal.value(d.anio)))
          .attr("cy", (d) => escalaY.value(d.no_casos))
          .attr("r", radio_circulo.value);
      },
      (exit) => {
        exit.remove();
      }
    );

  grupo_circulos_homicidios.value
    .selectAll("circle")
    .on("mouseover", abrirTooltipHomicidios)
    .on("mousemove", ajustarPosicionTooltip)
    .on("mouseleave", cerrarTooltip);

  grupo_circulos_desapariciones.value
    .selectAll("circle")
    .on("mouseover", abrirTooltipDesapariciones)
    .on("mousemove", ajustarPosicionTooltip)
    .on("mouseleave", cerrarTooltip);
}

const abrirTooltipHomicidios = function () {
  let seleccion = d3.select(this).datum();
  fenomeno_tultip.value = " homicidios diarios";
  numero_tultip.value = Math.round(seleccion.no_casos);
  anio_tultip.value = seleccion.anio;

  tooltipDiarios.value.style("visibility", "visible");
};

const abrirTooltipDesapariciones = function () {
  let seleccion = d3.select(this).datum();
  fenomeno_tultip.value = " desapariciones diarias";
  numero_tultip.value = Math.round(seleccion.no_casos);
  anio_tultip.value = seleccion.anio;

  tooltipDiarios.value.style("visibility", "visible");
};

const ajustarPosicionTooltip = function () {
  let x_position = 0;
  if (d3.pointer(event, document.body)[0] + 85 > ancho.value - 50) {
    x_position = d3.pointer(event, document.body)[0] - 120;
  } else {
    x_position = d3.pointer(event, document.body)[0] + 20;
  }
  tooltipDiarios.value
    .style("left", x_position + "px")
    .style("top", d3.pointer(event, document.body)[1] + "px")
    .on("mouseleave", cerrarTooltip);
};
const cerrarTooltip = function () {
  tooltipDiarios.value.style("visibility", "hidden");
};
</script>

<style scoped>
/*const color_dict = ref({ 
  desaparicion: "#d0a74e", 
  homicidio: "#ed594a" 
  });
*/
.nota {
  text-align: left;
  margin-left: 10px;
}
.pie {
  margin-top: 5px;
  margin-left: 10px;
  margin-bottom: 0px;
  text-align: left;
  font-style: italic;
}
.span-homicidios {
  color: #ed594a;
}

.span-desapariciones {
  color: #d0a74e;
}
.fuente {
  text-align: left;
  font-size: 12px;
  margin-left: 10px;
  margin-top: 2px;
}
.tultip {
  position: absolute;
  z-index: 2;
  background-color: #252323;
  color: white;
  opacity: 0.93;
  height: auto;
  width: 110px;
  font-size: 14px;
  padding: 5px;
}
.highlight {
  font-weight: bold;
}
</style>
